import { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const dropdownRef = useRef(null)
  const dropdownTimeoutRef = useRef(null)
  const location = useLocation()

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setDropdownOpen(false)
    setMobileDropdownOpen(false)
  }, [location.pathname])

  // Track scroll for sticky header backdrop styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
    }
  }, [])

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
      dropdownTimeoutRef.current = null
    }
    setDropdownOpen(true)
  }

  const handleDropdownMouseLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false)
    }, 150)
  }

  const isFestTyperActive =
    location.pathname === '/fest-typer' ||
    location.pathname === '/firmafest' ||
    location.pathname === '/privatfest' ||
    location.pathname === '/bryllup'

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 bg-black border-b border-white/10 ${
        scrolled
          ? 'shadow-2xl py-3.5'
          : 'py-4 sm:py-5'
      }`}
    >
      <div className="w-full px-6 md:px-12 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0 group focus:outline-none">
          <img
            src="https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/logo_1a594eb3.png"
            alt="Casper DJ"
            className="h-9 sm:h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation — strictly min-width 1200px */}
        <nav className="hidden min-[1200px]:flex items-center gap-8 text-xs font-semibold uppercase tracking-wider text-neutral-300" aria-label="Hovednavigation">
          {/* CASPER DJ / FORSIDE */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `transition-all duration-200 py-1 hover:text-white ${
                isActive
                  ? 'text-white underline underline-offset-8 decoration-2 font-bold'
                  : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            CASPER DJ
          </NavLink>

          {/* OM OS */}
          <NavLink
            to="/om-os"
            className={({ isActive }) =>
              `transition-all duration-200 py-1 hover:text-white ${
                isActive
                  ? 'text-white underline underline-offset-8 decoration-2 font-bold'
                  : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            OM OS
          </NavLink>

          {/* FEST TYPER Dropdown */}
          <div
            className="relative group py-2 -my-2"
            ref={dropdownRef}
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            <div className="flex items-center gap-1.5">
              <NavLink
                to="/fest-typer"
                className={`transition-all duration-200 py-1 hover:text-white ${
                  isFestTyperActive
                    ? 'text-white underline underline-offset-8 decoration-2 font-bold'
                    : 'text-neutral-300 hover:text-white'
                }`}
              >
                FEST TYPER
              </NavLink>
              <button
                type="button"
                onClick={() => setDropdownOpen((prev) => !prev)}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
                aria-label="Åbn menu for fest typer"
                className="py-1 text-neutral-400 hover:text-white transition-colors cursor-pointer"
              >
                <i
                  className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div
                className="absolute left-0 top-full pt-1.5 w-56 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                role="menu"
              >
                <div className="bg-neutral-950/98 backdrop-blur-xl border border-white/10 shadow-2xl p-2">
                  <NavLink
                    to="/fest-typer"
                    className={({ isActive }) =>
                      `block px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-white bg-white/10' : 'text-neutral-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                    role="menuitem"
                  >
                    Alle fest typer
                  </NavLink>
                  <div className="my-1 border-t border-white/10" />
                  <NavLink
                    to="/firmafest"
                    className={({ isActive }) =>
                      `block px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-white bg-white/10' : 'text-neutral-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                    role="menuitem"
                  >
                    Firmafest
                  </NavLink>
                  <NavLink
                    to="/privatfest"
                    className={({ isActive }) =>
                      `block px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-white bg-white/10' : 'text-neutral-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                    role="menuitem"
                  >
                    Privatfest
                  </NavLink>
                  <NavLink
                    to="/bryllup"
                    className={({ isActive }) =>
                      `block px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-white bg-white/10' : 'text-neutral-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                    role="menuitem"
                  >
                    Bryllup
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* GALLERI */}
          <NavLink
            to="/galleri"
            className={({ isActive }) =>
              `transition-all duration-200 py-1 hover:text-white ${
                isActive
                  ? 'text-white underline underline-offset-8 decoration-2 font-bold'
                  : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            GALLERI
          </NavLink>

          {/* KONTAKT */}
          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `transition-all duration-200 py-1 hover:text-white ${
                isActive
                  ? 'text-white underline underline-offset-8 decoration-2 font-bold'
                  : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            KONTAKT
          </NavLink>
        </nav>

        {/* Right CTA / Phone Actions — min-width 1200px */}
        <div className="hidden min-[1200px]:flex items-center gap-6">
          <a
            href="tel:+4530405006"
            className="flex items-center gap-2 text-xs font-semibold text-neutral-300 hover:text-white transition-colors tracking-wider whitespace-nowrap"
          >
            <i className="fa-solid fa-phone text-xs text-neutral-400" aria-hidden="true" />
            <span className="whitespace-nowrap">+45 30 40 50 06</span>
          </a>

          <Link
            to="/faa-et-tilbud"
            className="bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-2.5 hover:bg-neutral-200 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-sm hover:shadow-white/10 active:scale-95"
          >
            FÅ ET TILBUD
          </Link>
        </div>

        {/* Mobile / Tablet Actions (<1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-3">
          <a
            href="tel:+4530405006"
            className="hidden sm:flex p-2 sm:px-3 sm:py-2 text-xs font-semibold text-neutral-300 border border-white/20 items-center gap-1.5 hover:text-white hover:border-white/40 transition-colors whitespace-nowrap"
            aria-label="Ring til Casper DJ"
          >
            <i className="fa-solid fa-phone text-xs text-neutral-400" aria-hidden="true" />
            <span className="hidden sm:inline whitespace-nowrap">+45 30 40 50 06</span>
          </a>

          <Link
            to="/faa-et-tilbud"
            className="hidden sm:inline-block px-3.5 py-2 bg-white text-black font-bold text-xs uppercase tracking-wider hover:bg-neutral-200 transition-colors whitespace-nowrap"
          >
            Tilbud
          </Link>

          {/* Burger button — visible strictly below 1200px */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-expanded={mobileMenuOpen}
            aria-label="Åbn mobilmenu"
            className="p-2.5 bg-neutral-900 border border-white/10 text-neutral-300 hover:text-white transition-colors cursor-pointer"
          >
            <i
              className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-lg' : 'fa-bars text-lg'}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Mobile / Tablet Menu Drawer (<1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-white/10 bg-black/98 backdrop-blur-xl px-6 pt-5 pb-8 space-y-3 shadow-2xl animate-in fade-in slide-in-from-top-3 duration-200">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `block py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                isActive ? 'text-white underline underline-offset-4 decoration-2' : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            CASPER DJ
          </NavLink>

          <NavLink
            to="/om-os"
            className={({ isActive }) =>
              `block py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                isActive ? 'text-white underline underline-offset-4 decoration-2' : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            OM OS
          </NavLink>

          {/* Mobile Fest Typer Accordion */}
          <div>
            <div className="flex items-center justify-between py-1">
              <NavLink
                to="/fest-typer"
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider ${
                  isFestTyperActive ? 'text-white underline underline-offset-4 decoration-2' : 'text-neutral-300 hover:text-white'
                }`}
              >
                FEST TYPER
              </NavLink>
              <button
                type="button"
                onClick={() => setMobileDropdownOpen((prev) => !prev)}
                className="px-3 py-2 text-neutral-400 hover:text-white cursor-pointer"
                aria-label="Vis undersider for fest typer"
              >
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    mobileDropdownOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {mobileDropdownOpen && (
              <div className="ml-3 pl-3 border-l border-white/10 space-y-1 my-1">
                <NavLink
                  to="/firmafest"
                  className={({ isActive }) =>
                    `block py-2 text-xs font-medium uppercase tracking-wider transition-colors ${
                      isActive ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                    }`
                  }
                >
                  Firmafest
                </NavLink>
                <NavLink
                  to="/privatfest"
                  className={({ isActive }) =>
                    `block py-2 text-xs font-medium uppercase tracking-wider transition-colors ${
                      isActive ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                    }`
                  }
                >
                  Privatfest
                </NavLink>
                <NavLink
                  to="/bryllup"
                  className={({ isActive }) =>
                    `block py-2 text-xs font-medium uppercase tracking-wider transition-colors ${
                      isActive ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                    }`
                  }
                >
                  Bryllup
                </NavLink>
              </div>
            )}
          </div>

          <NavLink
            to="/galleri"
            className={({ isActive }) =>
              `block py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                isActive ? 'text-white underline underline-offset-4 decoration-2' : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            GALLERI
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `block py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                isActive ? 'text-white underline underline-offset-4 decoration-2' : 'text-neutral-300 hover:text-white'
              }`
            }
          >
            KONTAKT
          </NavLink>

          <div className="pt-4 border-t border-white/10 space-y-2">
            <a
              href="tel:+4530405006"
              className="flex items-center justify-center gap-2 w-full py-3 bg-neutral-900 border border-white/10 text-xs font-bold uppercase tracking-wider text-white hover:bg-neutral-800"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">+45 30 40 50 06</span>
            </a>
            <Link
              to="/faa-et-tilbud"
              className="flex items-center justify-center w-full py-3 bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-neutral-200 transition-colors"
            >
              FÅ ET TILBUD
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
